/**
 * Application-stage hooks (ENR-031/032/035/036).
 *
 * Wraps the Cases service Application-stage surface:
 *   - GET  /api/v1/cases/{id}/state-routing     → StateRouting (rules-engine decision)
 *   - GET  /api/v1/cases/{id}/plan-eligibility   → PlanEligibility (AI decision support, HITL)
 *   - GET  /api/v1/cases/{id}/packet             → StatePacket (App Builder packet)
 *   - POST /api/v1/cases/{id}/packet:submit       → PacketSubmission (submit to state)
 *
 * Each read degrades gracefully on 404 (`available = false`) so the case page
 * shows a friendly disabled state when a sibling endpoint is not yet wired,
 * mirroring useScreening / useEffectiveDate.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

// --- State routing (ENR-031) ------------------------------------------------ #

/** The rules-engine state-routing decision for a case. */
export interface StateRouting {
  /** Decided routing target (e.g. a state program / queue). */
  decision: string | null;
  /** Version of the routing rule set that produced the decision. */
  rule_version: string | null;
  /** Inputs the decision was computed from (label/value pairs). */
  inputs: { label: string; value: string }[];
}

interface UseStateRoutingResult {
  routing: StateRouting | null;
  available: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

export function useStateRouting(caseId: string | undefined): UseStateRoutingResult {
  const [routing, setRouting] = useState<StateRouting | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<StateRouting>(`/cases/${encodeURIComponent(caseId)}/state-routing`)
      .then((data) => {
        setRouting({ ...data, inputs: data.inputs ?? [] });
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setRouting(null);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setRouting(null);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { routing, available, isLoading, error, refresh };
}

// --- Plan eligibility (ENR-032, AI decision support / HITL) ----------------- #

/** AI decision-support output for plan eligibility (advisory). */
export interface PlanEligibility {
  /** Recommendation summary text. */
  recommendation: string | null;
  /** Model confidence 0–1 (drives the AIInsightCard indicator). */
  confidence: number | null;
  /** Supporting evidence / rationale lines. */
  evidence: string[];
  /** Optional capability id for audit linkage. */
  capability?: string | null;
}

interface UsePlanEligibilityResult {
  eligibility: PlanEligibility | null;
  available: boolean;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

export function usePlanEligibility(caseId: string | undefined): UsePlanEligibilityResult {
  const [eligibility, setEligibility] = useState<PlanEligibility | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<PlanEligibility>(`/cases/${encodeURIComponent(caseId)}/plan-eligibility`)
      .then((data) => {
        setEligibility({ ...data, evidence: data.evidence ?? [] });
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setEligibility(null);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setEligibility(null);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { eligibility, available, isLoading, error, refresh };
}

// --- App Builder packet (ENR-035/036) --------------------------------------- #

/** One form in the generated state packet. */
export interface PacketForm {
  id: string;
  name: string;
  /** `not_started` | `partial` | `complete`. */
  fill_status: string;
  /** Sequence/order in the submission packet. */
  sequence: number;
}

/** A required attachment for the packet. */
export interface PacketAttachment {
  id: string;
  label: string;
  attached: boolean;
}

/** The App Builder packet surface for a case. */
export interface StatePacket {
  forms: PacketForm[];
  attachments: PacketAttachment[];
  ready_to_submit: boolean;
  /** Receipt/confirmation once submitted, if any. */
  submitted_at?: string | null;
  receipt_id?: string | null;
}

/** Confirmation returned by the submit-to-state action. */
export interface PacketSubmission {
  receipt_id: string;
  submitted_at: string;
  status?: string | null;
}

interface UsePacketResult {
  packet: StatePacket | null;
  available: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  submit: () => Promise<PacketSubmission | null>;
}

export function usePacket(caseId: string | undefined): UsePacketResult {
  const [packet, setPacket] = useState<StatePacket | null>(null);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<StatePacket>(`/cases/${encodeURIComponent(caseId)}/packet`)
      .then((data) => {
        setPacket({
          ...data,
          forms: data.forms ?? [],
          attachments: data.attachments ?? [],
          ready_to_submit: !!data.ready_to_submit,
        });
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          setAvailable(false);
          setPacket(null);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setPacket(null);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const submit = useCallback(async (): Promise<PacketSubmission | null> => {
    if (!caseId) return null;
    setIsWorking(true);
    setActionError(null);
    try {
      const receipt = await apiClient.post<PacketSubmission>(
        `/cases/${encodeURIComponent(caseId)}/packet:submit`,
        {}
      );
      // Reflect submission locally, then refetch for the authoritative state.
      setPacket((p) =>
        p ? { ...p, submitted_at: receipt?.submitted_at ?? p.submitted_at, receipt_id: receipt?.receipt_id ?? p.receipt_id } : p
      );
      refresh();
      return receipt;
    } catch (err: unknown) {
      setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
      return null;
    } finally {
      setIsWorking(false);
    }
  }, [caseId, refresh]);

  return { packet, available, isLoading, isWorking, error, actionError, refresh, submit };
}
